import Decimal from 'decimal.js';
import { first, isEmpty } from 'lodash';
import { useContext, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useClientProfileDetailsContext } from '@/modules/entities/contexts/clientProfileDetails/clientProfileDetails.context';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { useTestamentaryEntityDetailsContext } from '@/modules/entities/contexts/testamentaryEntityDetails/testamentaryEntityDetails.context';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { FIRST_DECEDENT_ID_QUERY_PARAM } from '@/modules/entityDiagram/EntityDiagram.constants';
import { EstateWaterfallContext } from '@/modules/estateWaterfall/contexts/estateWaterfall.context';
import { isHypotheticalWaterfall } from '@/modules/estateWaterfall/EstateWaterfall.utils';
import {
  HouseholdDetailsContext,
  useHouseholdDetailsContext,
} from '@/modules/household/contexts/householdDetails.context';
import {
  EntityInEstateStatus,
  NonTrustEntityTaxStatus,
  TrustTaxStatus,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import {
  DispositiveProvisionsCalculateValues,
  DispositiveProvisionsContext,
} from '../contexts/dispositiveProvisions.context';
import {
  GetDispositiveProvisions_ClientOrganziationFragment,
  GetDispositiveProvisions_ClientProfileFragment,
  GetDispositiveProvisions_EntityFragment,
  GetDispositiveProvisions_EstateWaterfallFragment,
  GetDispositiveProvisions_TestamentaryEntityFragment,
  useGetDispositiveProvisionsQuery,
} from '../DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import { DispositiveProvisions_DispositionScenarioFragment } from '../graphql/DispositiveProvisions.fragments.generated';

// ------------------  Context Data Concrete Types -------------------

export type DispositiveProvisionsContextLoaded = Omit<
  DispositiveProvisionsContext,
  'formVariant'
>;

export type DispositiveProvisionsContextDataOrLoading =
  | ({ loading: false } & DispositiveProvisionsContextLoaded)
  | { loading: true };

// TODO add hypothetical disposition scenarios to the common entity subtype interface
interface HDS {
  hypotheticalDispositionScenarios: {
    edges: {
      node: DispositiveProvisions_DispositionScenarioFragment;
    }[];
  };
}

/* ------------------------------------------------------------------ */
/*                           Helper Types                             */
/* ------------------------------------------------------------------ */

// Generic argument interface for the context-builder helper functions.  The
// `TFragment` type parameter captures the specific GraphQL fragment passed to
// the builder.

interface ContextFnArgs<TFragment> {
  /** Household-level details shared by all targets */
  householdDetails: {
    primaryClients: HouseholdDetailsContext['primaryClients'];
    householdId: HouseholdDetailsContext['householdId'];
  };

  /** The concrete node/fragment we are building the DP context for */
  node: TFragment;

  /** Current market value for the node */
  value: Decimal;

  /** Tuple holding [showCalculatedDispositions, setter] */
  calculateTuple: DispositiveProvisionsCalculateValues;

  /** Optional associated waterfall */
  waterfall?: GetDispositiveProvisions_EstateWaterfallFragment;

  /** Optional explicitly provided first decedent id (e.g. from URL) */
  firstDecedentId?: string | null;
}

export function entityContext(
  args: ContextFnArgs<GetDispositiveProvisions_EntityFragment>
): DispositiveProvisionsContextDataOrLoading {
  const {
    householdDetails: { primaryClients, householdId },
    node: entity,
    value,
    calculateTuple: [showCalculatedDispositions, setShowCalculatedDispositions],
    waterfall,
    firstDecedentId,
  } = args;

  let taxStatus: TrustTaxStatus | NonTrustEntityTaxStatus | null = null;
  let estateStatus: EntityInEstateStatus | null = null;
  let gstStatus = null;
  const isOnHypotheticalWaterfall = isHypotheticalWaterfall(waterfall);

  const subtype = entity?.subtype;

  if ('trustTaxStatus' in subtype) {
    taxStatus = subtype.trustTaxStatus ?? null;
  }

  if ('nonTrustTaxStatus' in subtype) {
    taxStatus = subtype.nonTrustTaxStatus ?? null;
  }

  if ('nonTrustTaxStatusRequired' in subtype) {
    taxStatus = subtype.nonTrustTaxStatusRequired ?? null;
  }

  if ('inEstateStatus' in subtype) {
    estateStatus = subtype.inEstateStatus ?? null;
  }

  if ('gstStatus' in subtype) {
    gstStatus = subtype.gstStatus ?? null;
  }

  if (!entity || !primaryClients || !householdId) {
    return { loading: true };
  }

  const dispositionScenarios =
    (isOnHypotheticalWaterfall
      ? getNodes((entity.subtype as HDS).hypotheticalDispositionScenarios)
      : entity.subtype.dispositionScenarios) ?? [];

  if (isOnHypotheticalWaterfall && dispositionScenarios.length === 0) {
    // If we're on a hypothetical waterfall, but there are no hypothetical disposition scenarios,
    // we use the real disposition scenarios instead.
    dispositionScenarios.push(...(entity.subtype.dispositionScenarios ?? []));
  }

  const firstGrantorDeathId =
    waterfall?.firstGrantorDeath?.id ?? firstDecedentId ?? null;

  const beneficiaryDesignations =
    'beneficiaryDesignations' in entity.subtype
      ? entity.subtype.beneficiaryDesignations
      : null;

  return {
    loading: false,
    id: entity.id,
    firstGrantorDeathId,
    hasTestamentaryEntityBeenReviewed: false, // not relevant for a non-testamentary entity
    dispositionScenarios,
    beneficiaryDesignations,
    totalMarketValue: value,
    primaryClients: primaryClients,
    isTwoClientHousehold: primaryClients.length === 2,
    householdId,
    estateStatus,
    taxStatus,
    gstStatus,
    entityDisplayName: entity.subtype.displayName,
    entitySubtypeId: entity.subtype.id,
    entityStage: entity.stage,
    entityType: getEntityTypeFromEntityKind(entity.entityKind),
    isTestamentaryEntity: false,
    isClientProfile: false,
    clientProfileOrEntityOrTestamentaryEntityId: entity.id,
    allClientIds: primaryClients.map((g) => g.id),
    waterfall,
    isOnHypotheticalWaterfall,
    showCalculatedDispositions,
    setShowCalculatedDispositions,
    isEditingRootTarget: true,
  };
}

export function testamentaryEntityContext(
  args: ContextFnArgs<GetDispositiveProvisions_TestamentaryEntityFragment>
): DispositiveProvisionsContextDataOrLoading {
  const {
    householdDetails: { primaryClients, householdId },
    node: entity,
    value,
    calculateTuple: [showCalculatedDispositions, setShowCalculatedDispositions],
    waterfall,
    firstDecedentId,
  } = args;

  if (!entity || !primaryClients || !householdId) {
    return { loading: true };
  }
  const isOnHypotheticalWaterfall = isHypotheticalWaterfall(waterfall);

  let dispositionScenarios = entity.dispositionScenarios ?? [];
  const hypotheticalDispositionScenarios = getNodes(
    (entity as HDS).hypotheticalDispositionScenarios
  );

  if (isOnHypotheticalWaterfall && !isEmpty(hypotheticalDispositionScenarios)) {
    dispositionScenarios = hypotheticalDispositionScenarios;
  }

  const firstGrantorDeathId =
    waterfall?.firstGrantorDeath?.id ?? firstDecedentId ?? null;

  return {
    loading: false,
    id: entity.id,
    firstGrantorDeathId,
    hasTestamentaryEntityBeenReviewed: Boolean(entity.reviewedAt),
    dispositionScenarios,
    totalMarketValue: value,
    primaryClients: primaryClients,
    isTwoClientHousehold: primaryClients.length === 2,
    householdId,
    estateStatus: entity.inEstateStatus ?? null,
    taxStatus: entity.taxStatus ?? null,
    gstStatus: entity.gstStatus ?? null,
    entityDisplayName: entity.displayName,
    entitySubtypeId: null,
    entityStage: null,
    entityType: null,
    isTestamentaryEntity: true,
    isClientProfile: false,
    clientProfileOrEntityOrTestamentaryEntityId: entity.id,
    allClientIds: primaryClients.map((g) => g.id),
    waterfall,
    isOnHypotheticalWaterfall,
    showCalculatedDispositions,
    setShowCalculatedDispositions,
    isEditingRootTarget: true,
  };
}

export function clientProfileContext(
  args: ContextFnArgs<GetDispositiveProvisions_ClientProfileFragment>
): DispositiveProvisionsContextDataOrLoading {
  const {
    householdDetails: { primaryClients, householdId },
    node: cp,
    value,
    calculateTuple: [showCalculatedDispositions, setShowCalculatedDispositions],
    waterfall,
    firstDecedentId,
  } = args;

  if (!cp || !primaryClients || !householdId) {
    return { loading: true };
  }

  const isOnHypotheticalWaterfall = isHypotheticalWaterfall(waterfall);

  const dispositionScenarios =
    (isOnHypotheticalWaterfall
      ? getNodes(cp.hypotheticalDispositionScenarios)
      : cp.dispositionScenarios) ?? [];

  if (isOnHypotheticalWaterfall && dispositionScenarios.length === 0) {
    // If we're on a hypothetical waterfall, but there are no hypothetical disposition scenarios,
    // we use the real disposition scenarios instead.
    dispositionScenarios.push(...(cp.dispositionScenarios ?? []));
  }

  const firstGrantorDeathId =
    waterfall?.firstGrantorDeath?.id ?? firstDecedentId ?? null;

  // Determine estate status for client profile
  const isPrimaryClient = primaryClients.some((client) => client.id === cp.id);
  const clientEstateStatus = isPrimaryClient
    ? EntityInEstateStatus.InEstate
    : EntityInEstateStatus.OutOfEstate;

  return {
    loading: false,
    id: cp.id,
    firstGrantorDeathId,
    dispositionScenarios,
    hasTestamentaryEntityBeenReviewed: false, // not relevant for a non-testamentary entity
    totalMarketValue: value,
    primaryClients: primaryClients,
    isTwoClientHousehold: primaryClients.length === 2,
    householdId,
    estateStatus: clientEstateStatus,
    taxStatus: null,
    gstStatus: null,
    entityDisplayName: cp.displayName,
    entitySubtypeId: null,
    entityStage: null,
    entityType: null,
    isTestamentaryEntity: false,
    isClientProfile: true,
    clientProfileOrEntityOrTestamentaryEntityId: cp.id,
    allClientIds: primaryClients.map((g) => g.id),
    waterfall,
    isOnHypotheticalWaterfall,
    showCalculatedDispositions,
    setShowCalculatedDispositions,
    isEditingRootTarget: true,
  };
}

export function clientOrganizationContext(
  args: ContextFnArgs<GetDispositiveProvisions_ClientOrganziationFragment>
): DispositiveProvisionsContextDataOrLoading {
  const {
    householdDetails: { primaryClients, householdId },
    node,
    value,
    calculateTuple: [showCalculatedDispositions, setShowCalculatedDispositions],
    waterfall,
    firstDecedentId,
  } = args;

  if (!primaryClients || !householdId || !node) {
    return { loading: true };
  }

  const isOnHypotheticalWaterfall = isHypotheticalWaterfall(waterfall);

  const firstGrantorDeathId =
    waterfall?.firstGrantorDeath?.id ?? firstDecedentId ?? null;

  return {
    loading: false,
    id: node.id,
    primaryClients: primaryClients,
    isTwoClientHousehold: primaryClients.length === 2,
    firstGrantorDeathId,
    hasTestamentaryEntityBeenReviewed: false, // not relevant for a non-testamentary entity
    totalMarketValue: value,
    householdId,
    estateStatus: null,
    taxStatus: null,
    gstStatus: null,
    entityDisplayName: node.name,
    entitySubtypeId: null,
    entityStage: null,
    entityType: null,
    isTestamentaryEntity: false,
    isClientProfile: false,
    clientProfileOrEntityOrTestamentaryEntityId: node.id,
    allClientIds: primaryClients.map((g) => g.id),
    waterfall,
    isOnHypotheticalWaterfall,
    showCalculatedDispositions,
    setShowCalculatedDispositions,
    isEditingRootTarget: true,
  };
}

/**
 * Hook to fetch and return the context data for dispositive provisions.
 *
 * When used in the DispositiveProvisionsForms (V1 and V2), this hook fetches
 * the data for the "root" target, which is the Entity/TE/CP/CO that the
 * SplitScreen modal is opened for.
 *
 * @param opts - optional configuration object
 * @param opts.currentNodeValue - optional value of the target node. This is only
 * used for the ClientProfile and ClientOrganization targets.
 * @param opts.firstDeathPrimaryClientId - optional ID of the primary client who dies first.
 * If provided, this overrides the URL search param value.
 */
export function useDispositiveProvisionsContextData(opts?: {
  currentNodeValue?: Decimal;
  firstDeathPrimaryClientId?: string | null;
}): DispositiveProvisionsContextDataOrLoading {
  const { currentNodeValue, firstDeathPrimaryClientId } = opts ?? {};
  const { createErrorFeedback } = useFeedback();
  const showCalculatedDispositionsState = useState<boolean>(false);
  const householdDetails = useHouseholdDetailsContext();
  const entityDetails = useEntityDetailsContext();
  const testamentaryEntityDetails = useTestamentaryEntityDetailsContext();
  const clientProfileDetails = useClientProfileDetailsContext();
  const params = useParams();
  const waterfallId = params.waterfallId ?? null;

  const ewContext = useContext(EstateWaterfallContext);
  const [searchParams] = useSearchParams();
  // Use provided firstDeathPrimaryClientId if available, otherwise fall back to URL param.
  const firstDecedentId =
    firstDeathPrimaryClientId ||
    searchParams.get(FIRST_DECEDENT_ID_QUERY_PARAM);

  const id =
    ewContext?.state?.derivedData?.summaryNode?.data?.id ??
    testamentaryEntityDetails.entityId ??
    entityDetails?.entityId ??
    clientProfileDetails?.clientProfileId ??
    null;

  const {
    data: dispositiveProvisionsData,
    loading,
    error,
  } = useGetDispositiveProvisionsQuery({
    variables: {
      id: id!, // Guaranteed to be defined by the skip condition below
      waterfallWhere: {
        id: waterfallId,
      },
      isOnWaterfall: !!waterfallId,
      // Only scope to the waterfall when it is hypothetical; a base waterfall
      // must pass null so the resolver returns the live (base) designations.
      hypotheticalWaterfallId: ewContext?.state?.derivedData?.isHypothetical
        ? waterfallId
        : null,
    },
    onError: createErrorFeedback(
      'Failed to load entity data. Please refresh the page to try again.'
    ),
    skip: !id,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });

  if (loading) {
    return { loading: true };
  }

  if (error) {
    // because errorPolicy is 'all', data should still be present, so don't throw here
    diagnostics.error(
      'Caught error in useDispositiveProvisionsContextData',
      error
    );
  }

  if (!dispositiveProvisionsData) {
    diagnostics.warn(
      'dispositiveProvisionsData not present, but the load completed and there was no error'
    );
  }

  const node = dispositiveProvisionsData?.node;

  const waterfall = first(
    getNodes(dispositiveProvisionsData?.dispositiveProvisionWaterfalls)
  );

  if (!node?.__typename) return { loading: true };

  if (node.__typename === 'Entity') {
    return entityContext({
      householdDetails: householdDetails,
      node,
      value: node.subtype.currentValue,
      calculateTuple: showCalculatedDispositionsState,
      waterfall,
      firstDecedentId,
    });
  }

  if (node.__typename === 'TestamentaryEntity') {
    return testamentaryEntityContext({
      householdDetails: householdDetails,
      node,
      value: testamentaryEntityDetails.totalMarketValue,
      calculateTuple: showCalculatedDispositionsState,
      waterfall,
      firstDecedentId,
    });
  }

  if (node.__typename === 'ClientProfile') {
    return clientProfileContext({
      householdDetails: householdDetails,
      node,
      value: currentNodeValue ?? new Decimal(0), // Default to 0 if no value provided
      calculateTuple: showCalculatedDispositionsState,
      waterfall,
      firstDecedentId,
    });
  }

  if (node.__typename === 'ClientOrganization') {
    return clientOrganizationContext({
      householdDetails: householdDetails,
      node,
      value: currentNodeValue ?? new Decimal(0), // Default to 0 if no value provided
      calculateTuple: showCalculatedDispositionsState,
      waterfall,
      firstDecedentId,
    });
  }

  throw new Error(`Unhandled entity data ${node.__typename}`);
}
